import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { convertToMultiCurrencyAmount } from '@mews-distributor/common';
import { keyBy } from '@mews/fp';
import { PromotedServices, PromotedServicesResponse } from './types';

const dataInitialState: PromotedServices['data'] = {};

const metaInitialState: PromotedServices['meta'] = {
    hasFailed: false,
    isFetching: false,
};

const promotedServicesDataSlice = createSlice({
    name: 'promotedServicesData',
    initialState: dataInitialState,
    reducers: {
        fetchPromotedServicesSucceeded: (
            state,
            { payload: { promotedServices } }: PayloadAction<PromotedServicesResponse>
        ) => {
            const multicurrencyPromotedServices = promotedServices.map((promotedService) => ({
                ...promotedService,
                totalAmount:
                    promotedService.totalAmount != null
                        ? {
                              ...state[promotedService.promotedServiceAssignmentId]?.totalAmount,
                              ...convertToMultiCurrencyAmount(promotedService.totalAmount),
                          }
                        : null,
                unitAmount:
                    promotedService.unitAmount != null
                        ? {
                              ...state[promotedService.promotedServiceAssignmentId]?.unitAmount,
                              ...convertToMultiCurrencyAmount(promotedService.unitAmount),
                          }
                        : null,
                /**
                 * To prevent the occurrence of the error message "The item doesn't have a value for the selected currency"
                 * when switching currencies, we overwrite BE entity with MultiCurrencyAmount.
                 * One situation where this occurs is when the currency is changed through UI
                 * while passing the hourly service price into UpsellPrice.
                 *
                 * We could remove this overwrite by removing the usage of MultiCurrencyAmount in all the components. kanban-6216.
                 */
                minPricePerTimeUnit: {
                    ...state[promotedService.promotedServiceAssignmentId]?.minPricePerTimeUnit,
                    ...convertToMultiCurrencyAmount(promotedService.minPricePerTimeUnit),
                },
            }));

            return keyBy('promotedServiceAssignmentId', multicurrencyPromotedServices);
        },
    },
});

export const { fetchPromotedServicesSucceeded } = promotedServicesDataSlice.actions;

const promotedServicesMetaSlice = createSlice({
    name: 'promotedServicesMeta',
    initialState: metaInitialState,
    reducers: {
        fetchPromotedServicesStarted: (state) => ({ ...state, isFetching: true, hasFailed: false }),
        fetchPromotedServicesFailed: (state) => ({ ...state, isFetching: false, hasFailed: true }),
    },
    extraReducers: (builder) => {
        builder.addCase(fetchPromotedServicesSucceeded, (state) => ({ ...state, isFetching: false, hasFailed: false }));
    },
});

export const promotedServicesReducer = combineReducers({
    data: promotedServicesDataSlice.reducer,
    meta: promotedServicesMetaSlice.reducer,
});

export const { fetchPromotedServicesStarted, fetchPromotedServicesFailed } = promotedServicesMetaSlice.actions;
